import { LeadWithRelations } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ConversionReadinessCardProps {
  lead: LeadWithRelations;
}

export function ConversionReadinessCard({ lead }: ConversionReadinessCardProps) {
  const missingFields: string[] = [];
  
  if (!lead.service_type_id) missingFields.push('Service type');
  if (!lead.quoted_price) missingFields.push('Quoted price');
  if (!lead.scheduled_date) missingFields.push('Scheduled date');
  if (!lead.customer_email) missingFields.push('Email');

  const canConvert = lead.customer_name && lead.customer_phone && lead.customer_address && lead.issue_description;
  const isOptimal = canConvert && missingFields.length === 0;

  if (lead.status === 'converted') {
    return null;
  }

  return (
    <Card
      className={cn(
        'border-2',
        isOptimal
          ? 'border-success/30 bg-success/5'
          : canConvert
          ? 'border-warning/30 bg-warning/5'
          : 'border-destructive/30 bg-destructive/5'
      )}
    >
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          {isOptimal ? (
            <CheckCircle2 className="h-8 w-8 text-success shrink-0" />
          ) : (
            <AlertCircle className={cn(
              "h-8 w-8 shrink-0",
              canConvert ? "text-warning" : "text-destructive"
            )} />
          )}
          <div className="flex-1">
            <h3 className="font-semibold mb-1">
              {isOptimal
                ? 'Ready to Convert'
                : canConvert
                ? 'Can Convert (Missing Optional Info)'
                : 'Cannot Convert Yet'}
            </h3>
            {isOptimal ? (
              <p className="text-sm text-muted-foreground">
                All required and recommended information is present
              </p>
            ) : canConvert && missingFields.length > 0 ? (
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">Missing recommended information:</p>
                <ul className="list-disc list-inside space-y-1">
                  {missingFields.map((field, idx) => (
                    <li key={idx}>{field}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">Missing required information:</p>
                <ul className="list-disc list-inside space-y-1">
                  {!lead.customer_name && <li>Customer name</li>}
                  {!lead.customer_phone && <li>Phone number</li>}
                  {!lead.customer_address && <li>Customer address</li>}
                  {!lead.issue_description && <li>Issue description</li>}
                </ul>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
